import { Images as Files, SizeUnitType } from '@/interface';
import Compressor from 'compressorjs';
import { loggerError, loggerInfo } from '@/modules/logger';
import { isBlob } from '../detector';
import { LogModule } from '@/data/config';

/**
 * 新版 - 图片压缩
 * @param {file|Blob} file 上传文件
 * @param {Compressor.Options} options 上传参数，可参考
 * https://github.com/fengyuanchen/compressorjs
 * @returns
 */
export function imgCompressV2({
  file,
  options,
}: {
  file: File | Blob;
  options: Compressor.Options;
}): Promise<File | Blob> {
  return new Promise((reslove, reject) => {
    new Compressor(file, {
      maxHeight: 1000,
      maxWidth: 1000,
      ...options,
      success(result) {
        reslove(result);
      },
      error(err) {
        reject(err);
      },
    });
  });
}

/**
 * 设置全局默认值
 * @param {Compressor.Options} options 上传参数，可参考
 */
export function setDefaults(options: Compressor.Options) {
  return Compressor.setDefaults(options);
}

/**
 * @desc 根据单位换算
 * @param {number} size 大小
 * @param {Compressor.SizeUnitType} unit 当前单位
 */
export const getSizeByUnit = (size: number, unit: SizeUnitType) => {
  switch (unit) {
    case 'k':
    case 'K':
    case 'kb':
    case 'KB':
      return size * 1024;
    case 'm':
    case 'M':
    case 'mb':
    case 'MB':
      return size * 1024 * 1024;
    case 'g':
    case 'G':
    case 'gb':
    case 'GB':
      return size * 1024 * 1024 * 1024;
    default:
      return size * 1024;
  }
};

/**
 * @fileCompression
 * @desc 文件压缩二次封装
 * @param file 当前上传的文件
 * @param opt 文件压缩二次封装配置项
 * @param {ICompressOpt} opt.compressorOpt compressorjs库压缩配置,github地址: https://github.com/fengyuanchen/compressorjs
 * @param {IimgOpt} opt.imgOpt 图片配置项。
 * @param {IfileOpt} opt.fileOpt 文件配置项
 * @return {File | Blob | Files.ICompressErrType} 返回File或Blob,或者是失败错误码
 */
export const fileCompression = (
  file: File | Blob,
  opt?: Files.ICompressOpt
): Promise<Files.ICompressRes> => {
  const {
    imgOpt,
    fileOpt,
    compressorOpt = {},
    compressErrI18n = 'Compression abnormal',
  } = opt ?? {};
  // 图片类配置初始化
  const {
    imgMinSize = 0,
    imgMaxSize = 500,
    imgMinSizeUnit = 'kb',
    imgMaxSizeUnit = 'kb',
    incompressibleImgList = [],
    imgTooSmallErrI18n = '',
    imgTooLargeErrI18n = '',
  } = imgOpt ?? {};
  // 非图片类配置初始化
  const {
    fileMinSize = 0,
    fileMaxSize = 5,
    fileMinSizeUnit = 'mb',
    fileMaxSizeUnit = 'mb',
    fileTooSmallErrI18n = '',
    fileTooLargeErrI18n = '',
  } = fileOpt ?? {};
  return new Promise((reslove, reject) => {
    try {
      if (!isBlob(file)) {
        loggerError('The first argument must be a File or Blob object.', LogModule.Compress);
        throw new Error(compressErrI18n);
      }
      const fileSize = file.size;
      const isImage = ((value: string): boolean => /^image\/.+$/.test(value))(file.type);

      /**
       * @desc 验证文件 通过reslove 失败reject
       * @param {SizeIverify} param 所需要的验证参数
       */
      const verifyFile = (param: Files.ICompressSizeIverify) => {
        let success = true,
          errType: Files.ICompressErrType = '',
          errMsg = '';
        const { isImage, file, minSize, maxSize, minSizeUnit, maxSizeUnit } = param;
        if (fileSize < getSizeByUnit(minSize, minSizeUnit)) {
          success = false;
          errType = isImage ? 'imgMinErr' : 'fileMinErr';
          errMsg =
            (isImage ? imgTooSmallErrI18n : fileTooSmallErrI18n) ||
            `The ${file.type} cannot be smaller than ${minSize} ${minSizeUnit}, please re-upload`;
        } else if (fileSize > getSizeByUnit(maxSize, maxSizeUnit)) {
          success = false;
          errType = isImage ? 'imgMaxErr' : 'fileMaxErr';
          errMsg =
            (isImage ? imgTooLargeErrI18n : fileTooLargeErrI18n) ||
            `The ${file.type} cannot be greater than ${maxSize} ${maxSizeUnit}, please re-upload`;
        }
        const cres = { success, errType, errMsg, file };
        if (success) reslove(cres);
        reject(cres);
      };

      /**@imageProcessing 图片类型处理 */
      const imageProcessing = (): void => {
        const param: Files.ICompressSizeIverify = {
          file,
          isImage,
          minSize: imgMinSize,
          maxSize: imgMaxSize,
          minSizeUnit: imgMinSizeUnit,
          maxSizeUnit: imgMaxSizeUnit,
        };
        // 不支持压缩的图片,如gif动图不支持
        const incompressible = ['image/gif', ...incompressibleImgList];
        if (incompressible.indexOf(file.type) !== -1) {
          loggerInfo(`${file.type}不压缩,只做大小判断`, LogModule.Image);
          verifyFile(param);
          return;
        }
        loggerInfo(`${file.type}压缩之前的尺寸 ${fileSize}`, LogModule.Image);
        // 压缩处理
        new Compressor(file, {
          maxHeight: 1000,
          maxWidth: 1000,
          ...compressorOpt,
          success(res) {
            loggerInfo(`${file.type}压缩之后的尺寸${res.size}`, LogModule.Image);
            param.file = res.size <= fileSize ? res : file;
            verifyFile(param);
          },
          error(err) {
            loggerError(err?.message, LogModule.Compress);
            throw new Error(compressErrI18n);
          },
        });
      };

      /**@fileProcessing 文件类型处理 */
      const fileProcessing = () => {
        const param: Files.ICompressSizeIverify = {
          file,
          isImage,
          minSize: fileMinSize,
          maxSize: fileMaxSize,
          minSizeUnit: fileMinSizeUnit,
          maxSizeUnit: fileMaxSizeUnit,
        };
        loggerInfo(`${file.type} 不压缩,只做大小判断`, LogModule.File);
        verifyFile(param);
      };
      isImage ? imageProcessing() : fileProcessing();
    } catch (err: any) {
      loggerError(err?.message, LogModule.Compress);
      reject({ success: false, errType: 'compressErr', errMsg: compressErrI18n, file });
    }
  });
};
